import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle, KeyRound, Menu, X, Languages } from "lucide-react";
import { LogoBadge } from "./LogoBadge";
import { DEFAULT_WA_MESSAGE } from "../../lib/api";
import { useContact } from "../../hooks/useSettings";
import { useLang } from "../../lib/i18n";

const LINKS = [
    { href: "#home", key: "nav_home" },
    { href: "#inquiry", key: "nav_order" },
    { href: "#services", key: "nav_services" },
    { href: "#location", key: "nav_company" },
    { href: "/blog", key: "nav_blog", route: true },
    { href: "#contact", key: "nav_contact" },
];

const LangToggle = ({ compact = false }) => {
    const { lang, setLang } = useLang();
    return (
        <div
            className="flex items-center border border-white/15 rounded-full overflow-hidden"
            data-testid="lang-toggle"
        >
            <Languages className="w-3.5 h-3.5 text-amber-400 mr-2 ml-1 shrink-0" />
            <button
                onClick={() => setLang("ar")}
                data-testid="lang-toggle-ar"
                className={`${compact ? "px-3 py-2" : "px-2.5 py-1.5"} text-[11px] font-extrabold transition-colors duration-300 ${
                    lang === "ar" ? "bg-amber-400 text-[#0A1228]" : "text-slate-400 hover:text-white"
                }`}
            >
                عربي
            </button>
            <button
                onClick={() => setLang("en")}
                data-testid="lang-toggle-en"
                className={`${compact ? "px-3 py-2" : "px-2.5 py-1.5"} text-[11px] font-extrabold transition-colors duration-300 ${
                    lang === "en" ? "bg-amber-400 text-[#0A1228]" : "text-slate-400 hover:text-white"
                }`}
            >
                EN
            </button>
        </div>
    );
};

export const HeaderNav = () => {
    const [open, setOpen] = useState(false);
    const { mobile, callUrl, waUrl } = useContact();
    const { t } = useLang();

    return (
        <header
            data-testid="main-header-nav"
            className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0A1228]/85 border-b border-white/10"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
                <a href="#home" data-testid="nav-home-logo">
                    <LogoBadge />
                </a>

                <nav className="hidden lg:flex items-center gap-7">
                    {LINKS.map((l) =>
                        l.route ? (
                            <Link
                                key={l.href}
                                to={l.href}
                                data-testid={`nav-link-${l.href.slice(1)}`}
                                className="text-sm font-semibold text-slate-300 hover:text-amber-400 transition-colors duration-300"
                            >
                                {t(l.key)}
                            </Link>
                        ) : (
                            <a
                                key={l.href}
                                href={l.href}
                                data-testid={`nav-link-${l.href.slice(1)}`}
                                className="text-sm font-semibold text-slate-300 hover:text-amber-400 transition-colors duration-300"
                            >
                                {t(l.key)}
                            </a>
                        )
                    )}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <LangToggle />
                    <a
                        href={callUrl}
                        data-testid="nav-call-button"
                        className="flex items-center gap-2 text-xs font-mono-spec font-semibold text-slate-200 border border-white/15 rounded-full px-4 py-2 hover:border-amber-400/60 hover:text-amber-400 transition-colors duration-300"
                        dir="ltr"
                    >
                        <Phone className="w-3.5 h-3.5" />
                        {mobile}
                    </a>
                    <a
                        href={waUrl(DEFAULT_WA_MESSAGE)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="nav-whatsapp-button"
                        className="flex items-center gap-2 text-xs font-bold bg-emerald-500 text-[#0A1228] rounded-full px-4 py-2 hover:bg-emerald-400 transition-colors duration-300"
                    >
                        <MessageCircle className="w-3.5 h-3.5" />
                        {t("nav_whatsapp")}
                    </a>
                    <Link
                        to="/admin"
                        data-testid="nav-admin-button"
                        className="flex items-center gap-2 text-xs font-bold text-amber-400 border border-amber-400/40 rounded-full px-4 py-2 hover:bg-amber-400 hover:text-[#0A1228] transition-colors duration-300"
                    >
                        <KeyRound className="w-3.5 h-3.5" />
                        {t("nav_admin")}
                    </Link>
                </div>

                <div className="lg:hidden flex items-center gap-2">
                    <LangToggle compact />
                    <button
                        className="text-white p-2"
                        onClick={() => setOpen(!open)}
                        data-testid="nav-mobile-menu-toggle"
                        aria-label="القائمة"
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="lg:hidden overflow-hidden border-t border-white/10 bg-[#0A1228]/95"
                        data-testid="nav-mobile-drawer"
                    >
                        <div className="px-6 py-4 flex flex-col gap-4">
                            {LINKS.map((l) =>
                                l.route ? (
                                    <Link
                                        key={l.href}
                                        to={l.href}
                                        onClick={() => setOpen(false)}
                                        className="text-base font-semibold text-slate-200"
                                    >
                                        {t(l.key)}
                                    </Link>
                                ) : (
                                    <a
                                        key={l.href}
                                        href={l.href}
                                        onClick={() => setOpen(false)}
                                        className="text-base font-semibold text-slate-200"
                                    >
                                        {t(l.key)}
                                    </a>
                                )
                            )}
                            <div className="flex gap-3 pt-2">
                                <a
                                    href={callUrl}
                                    className="flex-1 text-center text-sm font-bold border border-white/15 rounded-full px-4 py-2.5 text-slate-200"
                                >
                                    {t("nav_direct_call")}
                                </a>
                                <Link
                                    to="/admin"
                                    className="flex-1 text-center text-sm font-bold text-amber-400 border border-amber-400/40 rounded-full px-4 py-2.5"
                                >
                                    {t("nav_admin")}
                                </Link>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};
